// V4-DL (spec-v4-downloads.md): "Download files", from the project's ••• menu. Every file the video is made of, each
// in its own format with a readable name: one row per file, its size on the right, a Download button each, grouped
// (finished video, frames, clips, voice, sounds, music, characters). "Download all" is the project pack (.zip) of the
// version in use. Phone first: a bottom sheet; on desktop the same sheet, wider.
import { useState } from "react";
import { api, exportLink } from "../api";
import { Download } from "../components/Download";
import { Sheet, Spinner, useLoad } from "../components/bits";
import { fileType, filesSummary, fmtBytes } from "../download";
import { toast } from "../store";
import type { Project } from "../types";

export function ProjectFilesSheet({ p, onClose }: { p: Project; onClose: () => void }) {
  const { data, error, loading, reload } = useLoad(() => api.projectFiles(p.id), [p.id]);
  const [packing, setPacking] = useState(false);

  const all = async () => {
    if (!data?.packRenderId) return;
    setPacking(true);
    try {
      const { url, filename } = await exportLink(data.packRenderId, "pack");
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast("Your project pack is downloading.", "good");
    } catch (e) {
      toast(`Couldn't make the pack. ${(e as Error).message}`, "bad");
    } finally {
      setPacking(false);
    }
  };

  return (
    <Sheet title="Download files" onClose={onClose} wide className="files-sheet">
      <div className="sheet-body">
        {loading && !data && <Spinner label="Finding your files" />}
        {error && !data && (
          <p className="files-error" role="alert">
            Couldn't list the files. {error}{" "}
            <button type="button" className="btn btn-quiet btn-small" onClick={reload}>
              Try again
            </button>
          </p>
        )}
        {data && !data.count && <p className="muted">Nothing to download yet. Pictures, clips, sounds and finished videos show up here as they're made.</p>}
        {data && !!data.count && (
          <>
            <div className="files-top">
              <p className="muted">Each file in its original format. {filesSummary(data.groups.flatMap((g) => g.files))}.</p>
              {data.packRenderId && (
                <button data-action="project.files" type="button" className="btn btn-primary btn-small" onClick={() => void all()} disabled={packing} data-autofocus title="The video in use, every picture, clip and sound, timelines and captions, in one .zip">
                  {packing ? "Preparing..." : "Download all (.zip)"}
                </button>
              )}
            </div>
            {data.groups.map((g) => (
              <section key={g.id} className="files-group" aria-labelledby={`files-${g.id}`}>
                <h3 id={`files-${g.id}`} className="files-group-head">
                  {g.label} <span className="muted">{filesSummary(g.files)}</span>
                </h3>
                <ul className="files-list">
                  {g.files.map((f) => (
                    <li key={f.id} className="files-row">
                      <span className="files-type" aria-hidden>
                        {fileType(f.fileName)}
                      </span>
                      <span className="files-text">
                        <span className="files-label">{f.label}</span>
                        <span className="files-name">{f.fileName}</span>
                      </span>
                      <span className="files-size">{fmtBytes(f.bytes)}</span>
                      <Download url={f.downloadUrl} name={f.fileName} label={f.label} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>
    </Sheet>
  );
}
